"use client";

import { MouseEventHandler } from "react";
import NextLink from "next/link";
import { PropertyDataV2 } from "commonModule/services/common";
import FavoriteButtonWrapper from "components/pages/vacation-rentals/FavoriteButtonWrapper";
import PriceWithUsd from "components/utilities/PriceWithUsd";
import useCurrency from "hooks/swr/useCurrency";
import { useIsAgent } from "hooks/useIsAgent";
import { selectIsUserLoggedIn } from "stores/auth/authSlice";
import { useAppSelector } from "stores/hooks";
import { selectSearchQuery } from "stores/search/searchSlice";
import { formatDateRangeWithoutLeadingZero } from "utils/dates";
import { getPropertyTypeDesc } from "utils/property-utils";
type Props = {
  property: PropertyDataV2;
  propertyUrl: string;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  setIsCloseOnOutsideEnabled?: (value: boolean) => void;
};
const TravelAgentPropertyCardBody = ({
  property,
  propertyUrl,
  onLinkClick,
  setIsCloseOnOutsideEnabled
}: Props) => {
  const searchQuery = useAppSelector(selectSearchQuery);
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const isAgent = useIsAgent();
  const {
    rate,
    formatter
  } = useCurrency();
  // const isVacationRentalPage = pathname?.startsWith("/vacation-rentals")
  const hidePriceOnVacationRentals = isLoggedIn && !isAgent;
  const checkIn = searchQuery?.startDate;
  const checkOut = searchQuery?.endDate;
  const hasDates = !!checkIn && !!checkOut;
  const nights = hasDates ? searchQuery?.noOfDays || Math.max(1, Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24))) : 0;
  const dateRangeLabel = hasDates ? formatDateRangeWithoutLeadingZero(checkIn, checkOut) : "";
  const nightsLabel = hasDates ? `${nights} ${nights === 1 ? "night" : "nights"}` : "";
  const showPrice = !hidePriceOnVacationRentals && isLoggedIn && hasDates && (property.totalBeforeTaxes ?? 0) > 0;
  const priceAmount = showPrice ? formatter(0, 0).format(property.totalBeforeTaxes * rate) : null;
  const priceMessage = hidePriceOnVacationRentals ? null : isLoggedIn ? "Add dates to see prices" : "Log in to see prices";
  const propertyTypeLabel = getPropertyTypeDesc(property?.propertyTypeDesc || "", property?.city || "", property?.state || "", property?.country || "");
  return <div className='flex flex-col gap-1.5' data-sentry-component="TravelAgentPropertyCardBody" data-sentry-source-file="TravelAgentPropertyCardBody.tsx">
      <div className='flex items-center gap-2'>
        <NextLink href={propertyUrl} target='_blank' onClick={onLinkClick} className='min-w-0 flex-1' data-sentry-element="NextLink" data-sentry-source-file="TravelAgentPropertyCardBody.tsx">
          <h3 className='truncate text-base font-semibold leading-[1.203] text-secondary-500 lg:text-sm lg:leading-[1.375]'>
            {propertyTypeLabel}
          </h3>
        </NextLink>
        {isLoggedIn && <div className='shrink-0'>
            <FavoriteButtonWrapper property={property} isListingPage setIsCloseOnOutsideEnabled={setIsCloseOnOutsideEnabled} />
          </div>}
      </div>

      <NextLink href={propertyUrl} target='_blank' onClick={onLinkClick} className='flex flex-col gap-1.5' data-sentry-element="NextLink" data-sentry-source-file="TravelAgentPropertyCardBody.tsx">
        {hasDates && <p className='truncate text-[11px] font-medium leading-none text-[#4B5A60] lg:text-[10px]'>
            <span className='font-semibold text-brand-dark'>
              {dateRangeLabel}
            </span>{" "}
            | {nightsLabel}
          </p>}

        <p className='truncate text-xs font-medium leading-[1.604] text-[#4B5A60] lg:text-[10px] lg:leading-[1.925]'>
          {property.noOfGuest} guests &middot; {property.noOfBedroom} bedrooms
          &middot; {property.noOfBathroom} bathrooms
        </p>

        <div className='flex min-h-[14px] items-center'>
          {priceAmount ? <p className='truncate leading-none text-brand-dark'>
              <span className='text-[15px] font-semibold tracking-[-0.02em]'>
                <PriceWithUsd price={priceAmount} currencyClassName='font-semibold' />
              </span>
              <span className='ml-1 text-sm font-medium'>total</span>
            </p> : priceMessage ? <p className={`truncate text-[11px] font-medium leading-none text-primary-500 lg:text-[10px] ${!isLoggedIn ? "underline" : ""}`}>
              {priceMessage}
            </p> : null}
        </div>
      </NextLink>
    </div>;
};
export default TravelAgentPropertyCardBody;